import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';

import PropTypes from 'prop-types';
import { devLog, devError } from '../../utils/devLog';
import { CommonCollections } from '../../App';
import ModalConfirmation from './ModalConfirmation';
import { axiosPost } from '../../utils/axiosPost';
import { useStaticCollections } from '../../context/StaticCollections';
import { useAdminCollections } from '../../context/AdminCollections';
import { useLoggedInUserContextProvider } from '../../context/LoggedInUserContext';
import { useRefereeCollections } from '../../context/RefereeReportCollections';
import ButtonIcon from './Forms/Elements/ButtonIcon';
import DownloadFile from './Forms/Elements/DownloadFile';
import {
  NOMINATION_STATUS,
  REFEREE_STATUS,
  strapiUrl,
} from '../../utils/constants';

const ActionButtons = ({
  row,
  onEmailApiErrorChange,
  isArchiveView = false,
}) => {
  const { originalUserDetails } = useLoggedInUserContextProvider();

  const {
    activeFellowsArray,
    electionTypes,
    titlesTypes,
    nonAasRefereesArray,
    refreshUsersByNonAasReferees,
  } = useStaticCollections();

  const { nominationArray } = useAdminCollections();

  const { setRefereeReportId } = useContext(CommonCollections);

  const { setNonAdminRefereeReportId } = useRefereeCollections();

  const [showModal, setShowModal] = useState(false);
  const [pendingApprovalOption, setPendingApprovalOption] = useState(null);
  const [refereeEmailType, setRefereeEmailType] = useState('');
  const [isEmailApiError, setIsEmailApiError] = useState(null);

  const {
    id,
    candidate,
    nominationId,
    refereeFellowId,
    refereeUser,
    refereeUserId,
    nonAasRefereeId,
    refereeStatusId,
    requestEmailDate,
    sectionalCommittee,
    nominationStatusTypeId,
    refereeArchived,
    reportFile,
    reportUploadedYear,
  } = row.original;

  let refereeEmail = '';

  if (refereeFellowId) {
    const fellow = activeFellowsArray.find(
      (item) => item.id === refereeFellowId,
    );

    if (fellow && fellow.attributes) {
      const { email } = fellow.attributes;
      refereeEmail = `(${email})`;
    }
  } else if (nonAasRefereeId) {
    const nonAasReferee = nonAasRefereesArray.find(
      (item) => item.id === nonAasRefereeId,
    );

    if (nonAasReferee && nonAasReferee.attributes) {
      const { email } = nonAasReferee.attributes;
      refereeEmail = `(${email})`;
    }
  }
  let canEdit = false;
  if (!refereeArchived) {
    if (
      refereeStatusId === REFEREE_STATUS.PENDING ||
      refereeStatusId === REFEREE_STATUS.EXTENSION_APPROVED ||
      refereeStatusId === REFEREE_STATUS.EXTENSION_REQUESTED
    ) {
      if (
        nominationStatusTypeId !== NOMINATION_STATUS.ELECTED &&
        nominationStatusTypeId !== NOMINATION_STATUS.EXPIRED &&
        nominationStatusTypeId !== NOMINATION_STATUS.SUSPENDED &&
        nominationStatusTypeId !== NOMINATION_STATUS.WITHDRAWN
      ) {
        canEdit = true;
      }
    }
  }
  const buttonIcon = canEdit
    ? 'bi bi-pen action-button'
    : 'bi bi-eye view-button';
  const buttonToolTipText = canEdit
    ? `Edit ${refereeUser}'s report`
    : `View ${refereeUser}'s report`;

  const handleClick = () => {
    setRefereeReportId(id);
  };

  const sendRefereeReportReminderEmail = () => {
    setRefereeEmailType('reminder');
    setShowModal(true);
  };

  const sendRefereeReportRequestEmail = () => {
    setRefereeEmailType('request');
    setShowModal(true);
  };

  const handleCancelSelect = () => {
    if (isEmailApiError) {
      setIsEmailApiError(null);
      onEmailApiErrorChange(null);
    }
    setShowModal(false);
  };

  const handleConfirmSelect = async () => {
    if (isEmailApiError) {
      setIsEmailApiError(null);
      onEmailApiErrorChange(null);
    }

    if (!Array.isArray(nominationArray) || nominationArray.length === 0) {
      devError('Data not loaded yet, please try again');
      setIsEmailApiError(true);
      onEmailApiErrorChange(true);
      return;
    }

    const activeFellow = activeFellowsArray.find(
      (fellow) => fellow.id === refereeFellowId,
    );

    const nonAasReferee = nonAasRefereesArray.find(
      (nonAasRefereeInstance) => nonAasRefereeInstance.id === nonAasRefereeId,
    );

    const refereeData = {};
    refereeData.nominationId = nominationId;
    refereeData.candidate = candidate;
    refereeData.referees = [
      {
        refereeUserId,
        refereeFellowId,
        nonAasRefereeId,
      },
    ];
    const refereeNominationTypeId = nominationArray.find(
      (item) => item.id === nominationId,
    );

    refereeData.sectionalCommittee =
      sectionalCommittee ??
      electionTypes[refereeNominationTypeId?.attributes?.electionTypeId];
    refereeData.testEmail = originalUserDetails.email;

    if (refereeEmailType === 'request') {
      refereeData.electionTypeId =
        refereeNominationTypeId?.attributes?.electionTypeId ?? '';
    }

    if (refereeEmailType === 'reminder') {
      refereeData.salutation = activeFellow?.attributes?.titleId
        ? titlesTypes[activeFellow?.attributes?.titleId]
        : titlesTypes[nonAasReferee?.attributes?.titleId];

      refereeData.proposer = `${refereeData.salutation ?? ''} ${refereeUser}`;
    }

    const maxRetries = 3;
    let retries = 0;
    let emailSent = false;

    while (retries < maxRetries && !emailSent) {
      const emailRefereeReportResponse = await axiosPost(
        `email-referee-report-${refereeEmailType}`,
        {
          data: refereeData,
        },
      );

      if (
        emailRefereeReportResponse?.status === 200 &&
        emailRefereeReportResponse?.data?.emailApiResponse?.[0]?.[0]?.Status ===
          'Accepted'
      ) {
        emailSent = true;

        setRefereeReportId(id);
        setNonAdminRefereeReportId(id);

        setIsEmailApiError(false);
        onEmailApiErrorChange(false);

        setTimeout(() => {
          setShowModal(false);
        }, 1500);
      } else {
        retries += 1;
        if (retries === maxRetries) {
          devLog(
            'Failed to send email after multiple attempts, setting error to true.',
          );
          setIsEmailApiError(true);
          onEmailApiErrorChange(true);
        } else {
          devLog(`Retrying... Attempt ${retries} of ${maxRetries}`);
        }
      }
    }
  };

  const approveReferee = async () => {
    setRefereeEmailType('request');
    setPendingApprovalOption('approve');
    setShowModal(true);
  };

  const declineReferee = async () => {
    setPendingApprovalOption('decline');
    setShowModal(true);
  };

  const removeNonAasRefereeSessionStorage = () => {
    const keyToRemove =
      'non-aas-referees?sort=lastName&pagination[limit]=6000&filters[isActive][$eq]=true';
    sessionStorage.removeItem(keyToRemove);
  };

  const approveRefereeConfirmSelect = async () => {
    const response = await axiosPost('referee-approve', {
      data: { id },
    });

    if (response && response?.status === 200) {
      removeNonAasRefereeSessionStorage();

      if (
        nonAasRefereeId &&
        typeof refreshUsersByNonAasReferees === 'function'
      ) {
        await refreshUsersByNonAasReferees([nonAasRefereeId]);
      }

      await handleConfirmSelect();
    }
  };

  const declineRefereeConfirmSelect = async () => {
    const response = await axiosPost('referee-decline', {
      data: { id },
    });

    if (response && response?.status === 200) {
      removeNonAasRefereeSessionStorage();

      if (
        nonAasRefereeId &&
        typeof refreshUsersByNonAasReferees === 'function'
      ) {
        await refreshUsersByNonAasReferees([nonAasRefereeId]);
      }

      setRefereeReportId(id);
      setNonAdminRefereeReportId(id);
    }
  };

  return (
    <>
      {requestEmailDate !== 'Pending approval' && !isArchiveView ? (
        <>
          <Link to="/referee-report-administration" onClick={handleClick}>
            <span title={buttonToolTipText}>
              <i className={buttonIcon} />
            </span>
          </Link>
          <ModalConfirmation
            showModal={showModal}
            setShowModal={setShowModal}
            handleCancelSelect={handleCancelSelect}
            bodyText={`Are you sure you want to send a **referee report ${refereeEmailType} email** to **${refereeUser}  ${refereeEmail}** for candidate **${candidate}?**`}
            handleConfirmSelect={handleConfirmSelect}
            rowId={`${id}`}
          />
        </>
      ) : null}

      {requestEmailDate !== 'Pending approval' && isArchiveView && !canEdit ? (
        <Link to="/referee-report-archive" onClick={handleClick}>
          <span title={buttonToolTipText}>
            <i className={buttonIcon} />
          </span>
        </Link>
      ) : null}

      {requestEmailDate === 'Pending approval' && !isArchiveView && (
        <>
          <ButtonIcon
            bootstrapIcon="bi bi-square"
            backgroundColor="bg-success"
            handleClick={approveReferee}
            handleKeyPress={approveReferee}
            tooltipText={`Approve referee ${refereeUser}`}
          />
          <ButtonIcon
            bootstrapIcon="bi bi-x-circle"
            backgroundColor="bg-danger"
            handleClick={declineReferee}
            handleKeyPress={declineReferee}
            tooltipText={`Decline referee ${refereeUser}`}
          />
          <ModalConfirmation
            showModal={showModal}
            setShowModal={setShowModal}
            handleCancelSelect={handleCancelSelect}
            bodyText={`Are you sure you want to **${pendingApprovalOption}** **${refereeUser}** as a referee?`}
            handleConfirmSelect={
              pendingApprovalOption === 'approve'
                ? approveRefereeConfirmSelect
                : declineRefereeConfirmSelect
            }
            rowId={`${id}`}
          />
        </>
      )}
      {reportFile && (
        <DownloadFile
          text={`Report Round Year : ${reportUploadedYear}`}
          url={`${strapiUrl}${reportFile}`}
          name=""
          buttonClassName="action-button-two"
          iconClassName="download-button"
          showFileName={false}
          showText={false}
        />
      )}

      {canEdit && requestEmailDate !== 'Pending approval' && !isArchiveView && (
        <>
          <Link onClick={sendRefereeReportRequestEmail}>
            <span title="Send Referee Report request Email">
              <i className="bi bi-envelope email-button" />
            </span>
          </Link>
          <Link onClick={sendRefereeReportReminderEmail}>
            <span title="Send Referee Report Reminder Email">
              <i className="bi bi-reply resend-button" />
            </span>
          </Link>
        </>
      )}
    </>
  );
};

ActionButtons.propTypes = {
  row: PropTypes.object,
  onEmailApiErrorChange: PropTypes.func.isRequired,
  isArchiveView: PropTypes.bool,
};

export default ActionButtons;
